/**
 * Fleet Dashboard (installer) — tiered progressive disclosure.
 * Tier 1 (always visible): Health + Energy Bar + Issues
 * Tier 2 (expanded by default): System Status List + Overnight Forecast
 * Tier 3 (collapsed by default): Recent Events + Grid Summary + Energy Position
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FleetHealthCard,
  FleetEnergyBar,
  FleetIssuesCard,
  SystemStatusList,
  OvernightForecastTable,
  getAtRiskCount,
  getLowestAtRisk,
  FleetRecentEvents,
  FleetActivityLog,
  FleetGridSummary,
} from '@/components/fleet';
import { CollapsibleSection } from '@/components/analytics/CollapsibleSection';
import { useFleetSystems } from '@/hooks/useFleetSystems';
import { useNotificationActivity } from '@/hooks/useNotificationActivity';
import { useApp } from '@/contexts/AppContext';
import { usePropertyTags } from '@/hooks/usePropertyTags';
import { useAdminOrgs } from '@/hooks/admin/useAdminOrgs';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { PropertyTagsEditor } from '@/components/property/PropertyTagsEditor';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Download, Loader2, Plus, RefreshCw } from 'lucide-react';
import { scrollToSection } from '@/lib/navigation';
import { toast } from 'sonner';
import {
  addSeverityToSystems,
  getFleetSummaryCounts,
} from '@/lib/fleet-severity';

export default function FleetDashboard() {
  const navigate = useNavigate();
  const { refreshData, selectedPropertyId, selectProperty, loading: appLoading, properties, userProfile, overrideOrgId, setOverrideOrgId, organization } = useApp();
  const isSuperAdmin = userProfile?.is_super_admin === true;
  const { data: adminOrgs } = useAdminOrgs(isSuperAdmin);

  const [propertyToRemove, setPropertyToRemove] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [editingTagsTarget, setEditingTagsTarget] = useState<{ id: string; name: string; tags: string[] } | null>(null);
  const [systemFilter, setSystemFilter] = useState<string | undefined>(undefined);

  const { data: fleetData, isLoading, error, refetch, isFetching } = useFleetSystems();
  const { activity: notificationEvents } = useNotificationActivity({ limit: 20 });

  // Enrich fleet systems with billing mode from properties context
  const systems = useMemo(() => {
    const raw = fleetData?.systems ?? [];
    const propMap = new Map(properties.map((p) => [p.id, p]));
    return raw.map((s) => {
      const prop = propMap.get(s.id);
      return {
        ...s,
        billingMode: (prop as { billing_mode?: string })?.billing_mode as 'direct' | 'reseller' | undefined,
        resellerBillingStatus: (prop as { reseller_billing_status?: string })?.reseller_billing_status as 'none' | 'pending' | 'active' | 'past_due' | 'canceled' | 'trialing' | undefined,
      };
    });
  }, [fleetData?.systems, properties]);

  const propertyIds = useMemo(() => systems.map((s) => s.id), [systems]);
  const { tagsByPropertyId } = usePropertyTags(propertyIds);

  const systemsWithSeverity = useMemo(
    () => addSeverityToSystems(systems),
    [systems]
  );

  const summaryCounts = useMemo(
    () => getFleetSummaryCounts(systemsWithSeverity),
    [systemsWithSeverity]
  );

  // Overnight risk metrics for conditional Tier 2/3 placement
  const atRiskCount = useMemo(() => getAtRiskCount(systemsWithSeverity), [systemsWithSeverity]);
  const lowestAtRisk = useMemo(() => getLowestAtRisk(systemsWithSeverity), [systemsWithSeverity]);

  const showSpinner = appLoading || (isLoading && !error);

  // Auto-trigger overnight forecasts for online systems missing them
  useEffect(() => {
    if (!fleetData?.systems?.length) return;
    const needForecast = [
      ...new Set(
        fleetData.systems
          .filter((s) => s.online && !s.hasOvernightForecast && s.hasTelemetry !== false)
          .map((s) => s.id)
      ),
    ];
    if (needForecast.length === 0) return;
    const STAGGER_MS = 400;
    const CAP = 25;
    (async () => {
      for (let i = 0; i < Math.min(needForecast.length, CAP); i++) {
        if (i > 0) await new Promise((r) => setTimeout(r, STAGGER_MS));
        const { error } = await supabase.functions.invoke('forecast-overnight', {
          method: 'POST',
          body: { propertyId: needForecast[i] },
        });
        if (error) {
          console.warn('[FleetDashboard] forecast-overnight skipped for property', needForecast[i], error.message);
        }
      }
      refetch();
    })();
  }, [fleetData?.systems, refetch]);

  const handleRemoveConfirm = async () => {
    if (!propertyToRemove) return;
    setRemoving(true);
    try {
      const { error: err } = await supabase.from('properties').delete().eq('id', propertyToRemove.id);
      if (err) throw err;
      toast.success('Property removed', { description: `${propertyToRemove.name} has been removed.` });
      setPropertyToRemove(null);
      if (selectedPropertyId === propertyToRemove.id) {
        selectProperty(null);
        navigate('/app/properties', { replace: true });
      }
      await refreshData();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not remove property');
    } finally {
      setRemoving(false);
    }
  };

  const setPropertyHidden = async (propertyId: string, isHidden: boolean) => {
    try {
      const { error: err } = await supabase
        .from('properties')
        .update({ is_hidden: isHidden })
        .eq('id', propertyId);
      if (err) throw err;
      toast.success(isHidden ? 'Property hidden' : 'Property visible again');
      await refreshData();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update visibility');
    }
  };

  const rowActions = {
    onHide: (propertyId: string) => setPropertyHidden(propertyId, true),
    onRemove: (id: string, name: string) => setPropertyToRemove({ id, name }),
    onEditTags: (system: (typeof systemsWithSeverity)[0]) =>
      () =>
        setEditingTagsTarget({
          id: system.id,
          name: system.systemName,
          tags: tagsByPropertyId.get(system.id) ?? [],
        }),
  };

  if (showSpinner) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-2">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="text-sm text-wt-text-secondary">
          {appLoading ? 'Loading\u2026' : 'Loading fleet\u2026'}
        </span>
      </div>
    );
  }

  const providerCount = new Set(systems.map((s) => s.provider)).size;

  // Notification summary for Tier 3
  const sentCount = notificationEvents.filter((e) => e.outcome === 'sent').length;
  const criticalEventCount = notificationEvents.filter((e) => e.severity === 'critical' && e.outcome === 'sent').length;
  const warningEventCount = notificationEvents.filter((e) => e.severity === 'warning' && e.outcome === 'sent').length;
  const eventsSummary = [
    `${sentCount} events today`,
    criticalEventCount > 0 ? `${criticalEventCount} critical` : null,
    warningEventCount > 0 ? `${warningEventCount} warning` : null,
  ].filter(Boolean).join(' \u00b7 ');

  // Fleet energy position for Tier 3
  const onlineSystems = systemsWithSeverity.filter((s) => s.online && s.hasTelemetry !== false);
  const totalCapacity = onlineSystems.reduce((sum, s) => sum + (s.batteryCapacityKwh ?? 0), 0);
  const totalAvailable = onlineSystems.reduce((sum, s) => sum + (s.availableKwh ?? 0), 0);
  const fleetSocPct = totalCapacity > 0 ? Math.round((totalAvailable / totalCapacity) * 100) : 0;

  // Grid summary collapsed text
  const totalGridImport = onlineSystems.reduce((sum, s) => sum + (s.gridImportKwhToday ?? 0), 0);
  const totalGridExport = onlineSystems.reduce((sum, s) => sum + (s.gridExportKwhToday ?? 0), 0);

  // Overnight forecast summary
  const overnightSummary = atRiskCount > 0
    ? `${atRiskCount} properties at risk tonight${lowestAtRisk ? ` \u00b7 Lowest: ${lowestAtRisk.name} at ${lowestAtRisk.kwh.toFixed(1)} kWh` : ''}`
    : 'All properties comfortable';

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-destructive">Could not load fleet snapshot.</p>
            {error.message && (
              <p className="text-xs text-muted-foreground mt-1 font-mono truncate" title={error.message}>
                {error.message}
              </p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="font-mono text-[28px] font-semibold tracking-tight text-foreground">
              Fleet Dashboard
            </h1>
            {isSuperAdmin && adminOrgs && adminOrgs.length > 0 && (
              <Select
                value={overrideOrgId ?? userProfile?.org_id ?? ''}
                onValueChange={(v) => {
                  if (v === userProfile?.org_id) {
                    setOverrideOrgId(null);
                  } else {
                    setOverrideOrgId(v);
                  }
                }}
              >
                <SelectTrigger className="w-[220px] h-8 text-xs bg-wt-bg-secondary border-wt-border">
                  <SelectValue placeholder="Select org..." />
                </SelectTrigger>
                <SelectContent>
                  {adminOrgs.map((org) => (
                    <SelectItem key={org.id} value={org.id} className="text-xs">
                      {org.name}
                      <span className="ml-1.5 text-wt-text-muted">({org.property_count})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {overrideOrgId && organization ? `${organization.name} · ` : ''}
            {properties.length} properties · {systems.length} systems · {providerCount} providers
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} title="Refresh data">
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="default" className="rounded-full font-mono" onClick={() => refetch()} disabled={isFetching}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            size="default"
            className="rounded-full font-mono bg-wt-green-primary hover:bg-wt-green-muted text-wt-bg-primary"
            onClick={() => navigate('/app/onboarding')}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Property
          </Button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════
          TIER 1: Always Visible — "Is my fleet healthy?"
          ═══════════════════════════════════════════ */}
      <div className="flex gap-5 flex-wrap">
        <FleetHealthCard counts={summaryCounts} onFilterClick={(status) => {
          const filterMap: Record<string, string> = { online: 'stable', warning: 'watch', offline: 'offline', stale: 'all' };
          setSystemFilter(filterMap[status] ?? 'all');
          setTimeout(() => scrollToSection('systems'), 100);
        }} />
        <FleetEnergyBar />
        <FleetIssuesCard counts={summaryCounts} events={notificationEvents} />
      </div>

      {/* ═══════════════════════════════════════════
          TIER 2: Expanded by Default — "What needs attention?"
          ═══════════════════════════════════════════ */}

      {/* 2A: System Status List */}
      <CollapsibleSection
        id="systems"
        title={`SYSTEMS (${summaryCounts.total})`}
        badge={summaryCounts.critical + summaryCounts.watch + summaryCounts.offline > 0
          ? summaryCounts.critical + summaryCounts.watch + summaryCounts.offline
          : undefined}
        collapsedSummary={
          `${summaryCounts.critical} critical \u00b7 ${summaryCounts.watch} watch \u00b7 ${summaryCounts.offline} offline`
        }
        defaultExpanded={true}
      >
        <SystemStatusList
          key={systemFilter ?? 'all'}
          systems={systemsWithSeverity}
          counts={summaryCounts}
          rowActions={rowActions}
          initialFilter={systemFilter as 'all' | 'critical' | 'watch' | 'offline' | 'stable' | undefined}
        />
      </CollapsibleSection>

      {/* 2B: Overnight Forecast — Tier 2 if at-risk > 0, Tier 3 otherwise */}
      {atRiskCount > 0 && (
        <CollapsibleSection
          title="OVERNIGHT FORECAST"
          badge={`${atRiskCount} at risk`}
          collapsedSummary={overnightSummary}
          defaultExpanded={true}
        >
          <OvernightForecastTable systems={systemsWithSeverity} />
        </CollapsibleSection>
      )}

      {/* ═══════════════════════════════════════════
          TIER 3: Collapsed by Default — "What happened recently?"
          ═══════════════════════════════════════════ */}

      {/* 3A: Overnight Forecast (only if no at-risk — moved to Tier 3) */}
      {atRiskCount === 0 && (
        <CollapsibleSection
          title="OVERNIGHT FORECAST"
          collapsedSummary={overnightSummary}
          defaultExpanded={false}
        >
          <OvernightForecastTable systems={systemsWithSeverity} />
        </CollapsibleSection>
      )}

      {/* 3B: Recent Events */}
      <CollapsibleSection
        title="RECENT EVENTS"
        badge={sentCount > 0 ? sentCount : undefined}
        collapsedSummary={eventsSummary || 'No events today'}
        defaultExpanded={false}
      >
        <div className="space-y-6">
          <FleetRecentEvents events={notificationEvents} />
          <FleetActivityLog
            events={notificationEvents}
            onViewAll={() => navigate('/app/alerts')}
          />
        </div>
      </CollapsibleSection>

      {/* 3C: Grid Summary */}
      <CollapsibleSection
        title="GRID SUMMARY (7-DAY)"
        collapsedSummary={
          `${totalGridImport.toFixed(0)} kWh imported \u00b7 ${totalGridExport.toFixed(0)} kWh exported \u00b7 Net: ${totalGridImport - totalGridExport >= 0 ? '+' : ''}${(totalGridImport - totalGridExport).toFixed(0)} kWh`
        }
        defaultExpanded={false}
      >
        <FleetGridSummary days={7} />
      </CollapsibleSection>

      {/* 3D: Fleet Energy Position */}
      <CollapsibleSection
        title="FLEET ENERGY POSITION"
        collapsedSummary={
          `${totalAvailable.toFixed(1)} kWh stored \u00b7 ${fleetSocPct}% average SOC`
        }
        defaultExpanded={false}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-6">
            <div>
              <p className="font-mono text-[28px] font-semibold text-foreground">{fleetSocPct}%</p>
              <p className="text-xs text-muted-foreground">Fleet SOC</p>
            </div>
            <div>
              <p className="font-mono text-[28px] font-semibold text-foreground">{totalAvailable.toFixed(1)}</p>
              <p className="text-xs text-muted-foreground">kWh available</p>
            </div>
            <div>
              <p className="font-mono text-[28px] font-semibold text-muted-foreground">{totalCapacity.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground">kWh capacity</p>
            </div>
          </div>
          <div className="h-4 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-status-online transition-all"
              style={{ width: `${fleetSocPct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
            <span>0 kWh</span>
            <span className="font-medium text-status-online">{totalAvailable.toFixed(1)} kWh</span>
            <span>{totalCapacity.toFixed(0)} kWh</span>
          </div>
        </div>
      </CollapsibleSection>

      {systems.length === 0 && (
        <div className="text-wt-text-secondary text-sm py-8 text-center">
          <p>No systems yet. Add a property and connect equipment to see them here.</p>
        </div>
      )}

      {/* Dialogs */}
      <AlertDialog open={!!propertyToRemove} onOpenChange={(open) => !open && setPropertyToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove property?</AlertDialogTitle>
            <AlertDialogDescription>
              {propertyToRemove && (
                <>
                  This will remove <strong>{propertyToRemove.name}</strong> and all its plants and equipment from Watt Tower.
                  Historical data will no longer be visible. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleRemoveConfirm();
              }}
              disabled={removing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing\u2026
                </>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editingTagsTarget && (
        <PropertyTagsEditor
          propertyId={editingTagsTarget.id}
          propertyName={editingTagsTarget.name}
          initialTags={editingTagsTarget.tags}
          open={true}
          onOpenChange={(open) => !open && setEditingTagsTarget(null)}
          onSaved={() => {
            setEditingTagsTarget(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}
